import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { useAuth } from '@/context/AuthContext';
import axios from 'axios';
import { 
  Award, Download, ArrowLeft, CheckCircle, BookOpen, Trophy, Share2
} from 'lucide-react';
import SocialShare from '@/components/SocialShare';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const StudentCertificates = () => {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const [loading, setLoading] = useState(true);
  const [certificates, setCertificates] = useState([]);
  const [courses, setCourses] = useState([]);
  const [selectedCert, setSelectedCert] = useState(null);
  const certRef = useRef(null);

  useEffect(() => {
    if (!authLoading && !user) {
      navigate('/student/login');
    } else if (user && user.role !== 'student') {
      navigate('/');
    } else if (user) {
      fetchData();
    }
  }, [user, authLoading, navigate]);

  const fetchData = async () => {
    try {
      const [certsRes, coursesRes] = await Promise.all([
        axios.get(`${API}/student/certificates`, { withCredentials: true }),
        axios.get(`${API}/student/courses`, { withCredentials: true })
      ]);
      setCertificates(certsRes.data);
      
      // Check completion status for each course
      const coursesWithStatus = await Promise.all(
        coursesRes.data.map(async (course) => {
          try {
            const completionRes = await axios.get(`${API}/student/course-completion/${course.id}`, { withCredentials: true });
            return { ...course, completion: completionRes.data };
          } catch {
            return { ...course, completion: null };
          }
        })
      );
      setCourses(coursesWithStatus);
    } catch (error) {
      if (error.response?.status === 401) {
        navigate('/student/login');
      }
    } finally {
      setLoading(false);
    }
  };

  const requestCertificate = async (courseId) => {
    try {
      const res = await axios.post(`${API}/student/certificates/request`, {
        course_id: courseId
      }, { withCredentials: true });
      toast.success('Certificate generated!');
      fetchData();
      setSelectedCert(res.data.certificate);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to generate certificate');
    }
  };

  // Sanitize text to prevent XSS
  const sanitizeText = (text) => {
    if (!text) return '';
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  };

  const downloadCertificate = () => {
    if (!certRef.current || !selectedCert) return;
    
    // Create a printable version using safe DOM manipulation
    const printWindow = window.open('', '_blank');
    if (!printWindow) {
      toast.error('Please allow popups to download certificate');
      return;
    }
    
    const doc = printWindow.document;
    
    // Create HTML structure safely
    const html = doc.createElement('html');
    const head = doc.createElement('head');
    const title = doc.createElement('title');
    title.textContent = `Certificate - ${selectedCert.course_title}`;
    
    const style = doc.createElement('style');
    style.textContent = `
      body { margin: 0; padding: 40px; font-family: 'Georgia', serif; }
      .certificate { 
        border: 8px double #0A1F44; 
        padding: 60px; 
        text-align: center;
        background: linear-gradient(135deg, #f5f7fa 0%, #e4e8ed 100%);
        max-width: 800px;
        margin: 0 auto;
      }
      .logo { font-size: 24px; color: #FF6A00; margin-bottom: 20px; }
      .title { font-size: 48px; color: #0A1F44; margin: 20px 0; }
      .subtitle { font-size: 18px; color: #666; margin-bottom: 40px; }
      .name { font-size: 36px; color: #0A1F44; font-weight: bold; margin: 20px 0; border-bottom: 2px solid #00AEEF; display: inline-block; padding: 10px 40px; }
      .course { font-size: 24px; color: #333; margin: 30px 0; }
      .details { font-size: 14px; color: #666; margin-top: 40px; }
      .cert-number { font-size: 12px; color: #999; margin-top: 20px; }
    `;
    
    head.appendChild(title);
    head.appendChild(style);
    
    const body = doc.createElement('body');
    const certificate = doc.createElement('div');
    certificate.className = 'certificate';
    
    // Create elements with textContent (safe from XSS)
    const logo = doc.createElement('div');
    logo.className = 'logo';
    logo.textContent = 'NEXTGEN TECH PATHWAYS';
    
    const certTitle = doc.createElement('div');
    certTitle.className = 'title';
    certTitle.textContent = 'Certificate of Completion';
    
    const subtitle1 = doc.createElement('div');
    subtitle1.className = 'subtitle';
    subtitle1.textContent = 'This certifies that';
    
    const name = doc.createElement('div');
    name.className = 'name';
    name.textContent = selectedCert.student_name || '';
    
    const subtitle2 = doc.createElement('div');
    subtitle2.className = 'subtitle';
    subtitle2.textContent = 'has successfully completed the course';
    
    const course = doc.createElement('div');
    course.className = 'course';
    course.textContent = selectedCert.course_title || '';
    
    const details = doc.createElement('div');
    details.className = 'details';
    
    const pDuration = doc.createElement('p');
    pDuration.textContent = `Duration: ${selectedCert.course_duration || ''}`;
    const pDate = doc.createElement('p');
    pDate.textContent = `Completion Date: ${selectedCert.completion_date || ''}`;
    const pRate = doc.createElement('p');
    pRate.textContent = `Completion Rate: ${selectedCert.completion_rate || 0}%`;
    
    details.appendChild(pDuration);
    details.appendChild(pDate);
    details.appendChild(pRate);
    
    const certNumber = doc.createElement('div');
    certNumber.className = 'cert-number';
    certNumber.textContent = `Certificate Number: ${selectedCert.certificate_number || ''}`;
    
    certificate.appendChild(logo);
    certificate.appendChild(certTitle);
    certificate.appendChild(subtitle1);
    certificate.appendChild(name);
    certificate.appendChild(subtitle2);
    certificate.appendChild(course);
    certificate.appendChild(details);
    certificate.appendChild(certNumber);
    
    body.appendChild(certificate);
    html.appendChild(head);
    html.appendChild(body);
    
    doc.appendChild(html);
    doc.close();
    printWindow.print();
  };

  if (authLoading || loading) {
    return (
      <div className="pt-20 min-h-screen bg-[#060F22] flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#00AEEF]"></div>
      </div>
    );
  }

  return (
    <div className="pt-20 min-h-screen bg-[#060F22]">
      <div className="max-w-6xl mx-auto px-6 lg:px-12 py-8">
        {/* Header */}
        <div className="flex items-center gap-4 mb-8">
          <button
            onClick={() => navigate('/student/dashboard')}
            className="p-2 text-[#94A3B8] hover:text-white rounded-lg hover:bg-[#0A1F44]"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-white flex items-center gap-3">
              <Award className="w-7 h-7 text-[#FF6A00]" />
              Certificates
            </h1>
            <p className="text-[#94A3B8]">Your achievements and certifications</p>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Earned Certificates */}
          <div>
            <h2 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
              <Trophy className="w-5 h-5 text-[#FF6A00]" />
              Earned Certificates
            </h2>
            {certificates.length === 0 ? (
              <div className="bg-[#0A1F44]/50 border border-[#00AEEF]/20 rounded-xl p-8 text-center">
                <Award className="w-16 h-16 text-[#94A3B8] mx-auto mb-4" />
                <p className="text-[#94A3B8]">No certificates yet</p>
                <p className="text-[#94A3B8] text-sm">Complete a course to earn your first certificate!</p>
              </div>
            ) : (
              <div className="space-y-4">
                {certificates.map((cert) => (
                  <div
                    key={cert.id}
                    onClick={() => setSelectedCert(cert)}
                    className={`bg-[#0A1F44]/50 border rounded-xl p-6 cursor-pointer transition-all ${
                      selectedCert?.id === cert.id 
                        ? 'border-[#FF6A00] shadow-[0_0_20px_rgba(255,106,0,0.2)]' 
                        : 'border-[#00AEEF]/20 hover:border-[#00AEEF]/50'
                    }`}
                  >
                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 rounded-xl bg-[#FF6A00]/20 flex items-center justify-center flex-shrink-0">
                        <Award className="w-6 h-6 text-[#FF6A00]" />
                      </div>
                      <div className="flex-1">
                        <h3 className="text-white font-bold">{cert.course_title}</h3>
                        <p className="text-[#94A3B8] text-sm">Completed: {cert.completion_date}</p>
                        <p className="text-[#00FFE0] text-xs mt-1">{cert.certificate_number}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Course Progress & Certificate Request */}
          <div>
            <h2 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
              <BookOpen className="w-5 h-5 text-[#00AEEF]" />
              Course Progress
            </h2>
            <div className="space-y-4">
              {courses.map((course) => (
                <div key={course.id} className="bg-[#0A1F44]/50 border border-[#00AEEF]/20 rounded-xl p-6">
                  <h3 className="text-white font-bold mb-2">{course.title}</h3>
                  
                  {course.completion ? (
                    <>
                      {/* Progress Bar */}
                      <div className="mb-4">
                        <div className="flex items-center justify-between text-sm mb-1">
                          <span className="text-[#94A3B8]">Completion</span>
                          <span className="text-[#00FFE0]">{course.completion.completion_rate}%</span>
                        </div>
                        <div className="w-full h-2 bg-[#060F22] rounded-full overflow-hidden">
                          <div 
                            className="h-full bg-gradient-to-r from-[#00AEEF] to-[#00FFE0]"
                            style={{ width: `${course.completion.completion_rate}%` }}
                          />
                        </div>
                      </div>

                      <div className="flex items-center justify-between text-sm mb-4">
                        <span className="text-[#94A3B8]">
                          {course.completion.completed_lessons}/{course.completion.total_lessons} lessons
                        </span>
                        <span className="text-[#94A3B8]">
                          {course.completion.passed_quizzes}/{course.completion.total_quizzes} quizzes
                        </span>
                      </div>

                      {course.completion.certificate_issued ? (
                        <div className="flex items-center gap-2 text-[#00FFE0]">
                          <CheckCircle className="w-5 h-5" />
                          <span>Certificate Issued</span>
                        </div>
                      ) : course.completion.can_get_certificate ? (
                        <button
                          onClick={() => requestCertificate(course.id)}
                          data-testid={`request-cert-${course.id}`}
                          className="w-full bg-[#FF6A00] text-white font-medium px-4 py-2 rounded-xl hover:shadow-[0_0_20px_rgba(255,106,0,0.3)] transition-all flex items-center justify-center gap-2"
                        >
                          <Award className="w-4 h-4" />
                          Get Certificate
                        </button>
                      ) : (
                        <p className="text-[#94A3B8] text-sm">
                          Complete at least 80% and pass all quizzes to earn certificate
                        </p>
                      )}
                    </>
                  ) : (
                    <p className="text-[#94A3B8]">Loading progress...</p>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Certificate Preview Modal */}
        {selectedCert && (
          <div className="mt-8">
            <div className="bg-[#0A1F44]/50 border border-[#FF6A00]/30 rounded-xl overflow-hidden">
              <div className="p-4 border-b border-[#00AEEF]/10 flex items-center justify-between flex-wrap gap-4">
                <h3 className="text-white font-bold">Certificate Preview</h3>
                <div className="flex items-center gap-3">
                  <SocialShare
                    type="certificate"
                    title={selectedCert.course_title}
                    description={`Completed with ${selectedCert.completion_rate}% completion rate`}
                    shareUrl={`/certificates/verify/${selectedCert.certificate_number}`}
                    variant="compact"
                  />
                  <button
                    onClick={downloadCertificate}
                    className="flex items-center gap-2 bg-[#FF6A00] text-white px-4 py-2 rounded-xl hover:bg-[#FF6A00]/80"
                  >
                    <Download className="w-4 h-4" />
                    Download / Print
                  </button>
                </div>
              </div>
              <div ref={certRef} className="p-8 bg-gradient-to-br from-[#f5f7fa] to-[#e4e8ed]">
                <div className="border-8 border-double border-[#0A1F44] p-12 text-center">
                  <p className="text-2xl text-[#FF6A00] font-bold mb-4">NEXTGEN TECH PATHWAYS</p>
                  <h2 className="text-5xl font-serif text-[#0A1F44] mb-4">Certificate of Completion</h2>
                  <p className="text-lg text-gray-600 mb-8">This certifies that</p>
                  <p className="text-4xl font-bold text-[#0A1F44] border-b-2 border-[#00AEEF] inline-block px-8 pb-2 mb-8">
                    {selectedCert.student_name}
                  </p>
                  <p className="text-lg text-gray-600 mb-4">has successfully completed the course</p>
                  <p className="text-3xl font-bold text-[#0A1F44] mb-8">{selectedCert.course_title}</p>
                  <div className="text-sm text-gray-600 space-y-1">
                    <p>Duration: {selectedCert.course_duration}</p>
                    <p>Completion Date: {selectedCert.completion_date}</p>
                    <p>Completion Rate: {selectedCert.completion_rate}%</p>
                  </div>
                  <p className="text-xs text-gray-400 mt-8">Certificate Number: {selectedCert.certificate_number}</p>
                  <p className="text-xs text-[#00AEEF] mt-2">
                    Verify at: next-gen-code.preview.emergentagent.com/certificates/verify/{selectedCert.certificate_number}
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default StudentCertificates;
